import * as actions from '@actions';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'wrapped-redux';
import { getRuntimeConfig } from 'vl-common/src/hooks/Runtime';
import { useUserSafe } from '@vl-core/hooks/useUser';

const publicRuntimeConfig = getRuntimeConfig();

export type Article = {
  title: string;
  body: string;
};

function unifyArticle(
  article: { title: string; body: { view: { value: string } } } | { title: string; template_body: string },
  fromConfluence = true
): Article | undefined {
  if (fromConfluence && 'body' in article) {
    return { title: article.title, body: article.body.view.value };
  }

  if ('template_body' in article) {
    return { title: article.title, body: article.template_body };
  }

  return undefined;
}

async function getNoAuthArticle(category: string, dispatch: ReturnType<typeof useDispatch>) {
  const shouldUseConfluence = publicRuntimeConfig.CONFLUENCE === '1';

  if (shouldUseConfluence) {
    const result = await dispatch(actions.fetchArticleNoAuth(category));
    return unifyArticle(result, true);
  }

  const result = await dispatch(actions.fetchArticleNoAuthFromDB(category));
  return unifyArticle(result, false);
}

async function getAuthenticatedArticle(category: string, dispatch: ReturnType<typeof useDispatch>) {
  const article = await dispatch(actions.getArticleNo(category));
  const shouldUseConfluence = publicRuntimeConfig.CONFLUENCE === '1';

  if (!article?.confluence?.length) return undefined;

  const confluenceId = article.confluence[0].confluence_id;
  const templateCode = article.confluence[0].content_template_code;

  const result = shouldUseConfluence
    ? await dispatch(actions.fetchArticle(confluenceId))
    : await dispatch(actions.fetchArticleFromDB(templateCode));

  return unifyArticle(result, !shouldUseConfluence);
}

const commonCategories = [
  'cookies',
  'accessibility',
  'regulatory',
  'services',
  'restricted_access_services',
  'cookies_essential',
  'cookies_functional',
  'cookies_performance',
  'system_warning'
];

async function fetchArticle(
  user: {
    policies_accepted: boolean;
    user_type_code: string;
  },
  category: string,
  dispatch: ReturnType<typeof useDispatch>
) {
  const isCommonCategory = commonCategories.includes(category);

  try {
    if (!user || isCommonCategory) {
      return await getNoAuthArticle(category, dispatch);
    }

    const { policies_accepted, user_type_code } = user;
    const isClinician = user_type_code === 'CLINICIAN';

    if (policies_accepted || isClinician) {
      return await getAuthenticatedArticle(category, dispatch);
    }

    return undefined;
  } catch {
    return undefined;
  }
}

export default function useArticle(category: string, noAuthFallback = false): Article | undefined {
  const user = useUserSafe();
  const dispatch = useDispatch();
  const result = useQuery({
    queryKey: ['useArticle', category],
    queryFn: () => fetchArticle(user || { policies_accepted: false, user_type_code: '' }, category, dispatch),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !!user || noAuthFallback
  });

  return result.data;
}
